import { Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import { KalaApiResponse } from '../models/kala-api.model';
import {
	LegacyUserSettingPayload,
	UserSettingBatchPayload,
	UserSettingResponse
} from '../models/user-setting-api.model';
import { BaseService } from './base.service';

/**
 * Remote store for user preferences: `GET /userSettings?key=` to read,
 * `PUT /userSettings?key=` to write one record, and `PUT /userSettings/batch` to
 * write several as one commit.
 *
 * A record is stored as the settings object itself — `PUT ?key=…-theme` sends
 * `{"theme":"classic"}`, not a `{ key, value }` wrapper around escaped text —
 * so callers hand over and receive plain objects. A key that was never written
 * reads back as `null` rather than an error: a first-time user is not a
 * failure, and callers fall back to their defaults.
 *
 * Reads accept every shape the API has answered with — enveloped (`data: …`)
 * or bare, the object itself, a legacy `{ key, value }` text payload, or bare
 * JSON text as {@link UserSettingsService.saveBatch} writes it — because
 * misreading a stored record as `null`, or handing text to a caller expecting
 * an object, would have callers overwrite real settings with defaults.
 *
 * Like the rest of the kala API, business failures arrive as `200 OK` with
 * `isSuccessful: false`, so the envelope decides success.
 */
@Service()
export class UserSettingsService extends BaseService {
	private get userSettingsUrl(): string {
		return `${this.kalaApiPath}/userSettings`;
	}

	private get userSettingsBatchUrl(): string {
		return `${this.userSettingsUrl}/batch`;
	}

	/** @returns the stored value, or `null` when the key has never been written. */
	get<T>(key: string): Observable<T | null> {
		return this.http.get<UserSettingResponse>(this.userSettingsUrl, { params: { key } }).pipe(
			map(response => {
				assertSuccessful(response, 'دریافت تنظیمات انجام نشد');

				return readSettingValue<T>(response);
			})
		);
	}

	save<T>(key: string, value: T): Observable<void> {
		return this.http.put<KalaApiResponse<unknown>>(this.userSettingsUrl, value, { params: { key } }).pipe(
			map(response => {
				assertSuccessful(response, 'ذخیره تنظیمات انجام نشد');
			})
		);
	}

	/**
	 * Write several records at once — `PUT /userSettings/batch`, keyed exactly as
	 * {@link save} keys a single one.
	 *
	 * For a screen that saves more than one group: the تنظیمات page promotes the
	 * theme, تنظیمات عمومی, the order defaults and مبنای قیمت together, and a
	 * trader who changed two of them must not end up with one saved and the other
	 * not. One request means the server accepts or rejects the lot, which a fan of
	 * parallel writes cannot promise.
	 *
	 * Each record goes up as JSON text, the shape the endpoint declares; a record
	 * written this way reads back through {@link get} like any other.
	 */
	saveBatch(records: Record<string, unknown>): Observable<void> {
		const payload: UserSettingBatchPayload = Object.fromEntries(
			Object.entries(records).map(([key, value]) => [key, JSON.stringify(value)])
		);

		return this.http.put<KalaApiResponse<unknown>>(this.userSettingsBatchUrl, payload).pipe(
			map(response => {
				assertSuccessful(response, 'ذخیره تنظیمات انجام نشد');
			})
		);
	}
}

/**
 * Only an explicit `isSuccessful: false` counts as a failure. The write answers
 * `204 No Content`, and a body that never arrived cannot be read as a rejection
 * — the HTTP status already said the call worked, and treating it as an error
 * would leave the cache holding values the API has since accepted.
 */
function assertSuccessful(response: Partial<KalaApiResponse<unknown>> | null, fallbackMessage: string): void {
	if (response?.isSuccessful === false) {
		throw new Error(response.error?.message ?? fallbackMessage);
	}
}

/** Take the stored record from whichever shape the read answered with. */
function readSettingValue<T>(response: UserSettingResponse | null): T | null {
	if (!response) {
		return null;
	}

	const record = 'data' in response ? response.data : response;

	if (record === null || record === undefined) {
		return null;
	}

	// A legacy record keeps its payload as JSON text; a current one is the object.
	if (typeof record === 'object' && typeof (record as LegacyUserSettingPayload).value === 'string') {
		return parseSettingValue<T>((record as LegacyUserSettingPayload).value);
	}

	// A record written by `saveBatch` went up as JSON text, so it can come back as
	// bare text rather than wrapped — the same key then holds one shape or the
	// other depending on which write path last touched it, and handing the text
	// straight to a caller that expects an object drops every setting to its
	// default. Text that does not parse is a value that simply is a string (a
	// theme name), not a corrupt record: bare, the two are indistinguishable, so
	// the raw string stands. Inside the legacy wrapper `value` is declared to be
	// JSON text, and a parse failure there still means corrupt.
	if (typeof record === 'string') {
		return parseSettingValue<T>(record) ?? (record as T);
	}

	return record as T;
}

/**
 * A stored value that no longer parses is treated as absent: the caller's
 * defaults are a better answer than blocking the user on corrupt server data.
 */
function parseSettingValue<T>(raw: string | null | undefined): T | null {
	if (!raw) {
		return null;
	}

	try {
		const parsed: unknown = JSON.parse(raw);

		if (typeof parsed !== 'string') {
			return (parsed as T) ?? null;
		}

		// A string can mean two things: a double-encoded record, whose text
		// parses again, or a value that simply is a string — `"dark"` for the
		// theme. Only the first gets a second parse, and a failure there means
		// it was the second, not a broken record.
		try {
			return JSON.parse(parsed) as T;
		} catch {
			return parsed as T;
		}
	} catch {
		return null;
	}
}
